import { useCallback, useEffect, useRef } from 'react';

import { endpoints } from '~/api/endpoints';
import { createSSEStream } from '~/api/sse-client';
import { mcpApiBaseUrl } from '~/constants/config/env';
import type {
  ContentBlock,
  ContentBlockDeltaData,
  ContentBlockStartData,
  MessageCompleteData,
  MessageStartData,
  SSEErrorData,
  SSEEvent,
} from '~/features/chat/types/chat.types';
import { normalizeMessageContent } from '~/features/chat/types/chat.types';
import { friendlyChatError } from '~/features/chat/utils/friendly-chat-error';

import { useAgentPanelStore } from './use-agent-panel-store';

const FLUSH_INTERVAL_MS = 50;

interface UseAgentPanelStreamReturn {
  /**
   * Opens the send-message SSE stream. `extraBody` is merged into the POST
   * body for callers that need request-scoping fields the ai-mcp-server
   * accepts alongside `content` — e.g. `experiment_id` / `tenant_id` for the
   * experiment-details analytics chat. `content` always wins on key conflict.
   */
  startStream: (
    conversationId: string,
    content: string,
    extraBody?: Record<string, unknown>,
  ) => void;
  abortStream: () => void;
}

/** Minimal abort handle — the real `createSSEStream` returns an `AbortController`,
 * but tests inject a plain `{ abort }` mock; we only ever call `.abort()`. */
interface AbortHandle {
  abort: () => void;
}

/**
 * SSE driver scoped to a single agent-panel context. Mirrors the shape of
 * `useChatStream` but dispatches into `useAgentPanelStore` keyed by `contextKey`,
 * so streams from different pages don't trample each other's state.
 */
export function useAgentPanelStream(contextKey: string): UseAgentPanelStreamReturn {
  const abortRef = useRef<AbortHandle | null>(null);
  const bufferRef = useRef(new Map<number, string>());
  const flushTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activeMessageIdRef = useRef<string | null>(null);

  const flushBuffer = useCallback(() => {
    const messageId = activeMessageIdRef.current;
    if (!messageId) return;
    const store = useAgentPanelStore.getState();
    bufferRef.current.forEach((delta, blockIndex) => {
      if (delta) store.appendDelta(contextKey, messageId, blockIndex, delta);
    });
    bufferRef.current.clear();
  }, [contextKey]);

  const startFlushTimer = useCallback(() => {
    if (flushTimerRef.current) return;
    flushTimerRef.current = setInterval(flushBuffer, FLUSH_INTERVAL_MS);
  }, [flushBuffer]);

  const stopFlushTimer = useCallback(() => {
    if (flushTimerRef.current) {
      clearInterval(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    flushBuffer();
  }, [flushBuffer]);

  const handleEvent = useCallback(
    (event: SSEEvent) => {
      const store = useAgentPanelStore.getState();

      switch (event.event) {
        case 'message_start': {
          const data = event.data as unknown as MessageStartData;
          activeMessageIdRef.current = data.messageId;
          const ctx = store.getOrInitContext(contextKey);
          if (ctx.streamingMessageId && ctx.streamingMessageId !== data.messageId) {
            store.updateMessage(contextKey, ctx.streamingMessageId, { id: data.messageId });
            store.setStreaming(contextKey, true, data.messageId);
          }
          startFlushTimer();
          break;
        }
        case 'content_block_start': {
          const data = event.data as unknown as ContentBlockStartData;
          const messageId = activeMessageIdRef.current;
          if (!messageId) break;
          const empty = createEmptyBlock(data.blockType);
          if (empty) {
            const ctx = store.getOrInitContext(contextKey);
            const msg = ctx.messages.find((m) => m.id === messageId);
            if (msg) {
              const content = [...msg.content];
              content[data.blockIndex] = empty;
              store.updateMessage(contextKey, messageId, { content });
            }
          }
          break;
        }
        case 'content_block_delta': {
          const data = event.data as unknown as ContentBlockDeltaData;
          const existing = bufferRef.current.get(data.blockIndex) ?? '';
          bufferRef.current.set(data.blockIndex, existing + data.delta);
          break;
        }
        case 'content_block_stop': {
          flushBuffer();
          break;
        }
        case 'message_complete': {
          const data = event.data as unknown as MessageCompleteData;
          stopFlushTimer();
          // The live server sends `content` as a string; normalize to
          // ContentBlock[] (falling back to the accumulated blocks) so we
          // never overwrite the store's array with a raw string.
          const normalized = normalizeMessageContent(data.message.content);
          store.updateMessage(contextKey, data.messageId, {
            ...data.message,
            content:
              normalized ??
              store.getOrInitContext(contextKey).messages.find((m) => m.id === data.messageId)
                ?.content ??
              [],
            status: 'complete',
          });
          store.setStreaming(contextKey, false);
          activeMessageIdRef.current = null;
          break;
        }
        case 'error': {
          const data = event.data as unknown as SSEErrorData;
          stopFlushTimer();
          const messageId =
            activeMessageIdRef.current ?? store.getOrInitContext(contextKey).streamingMessageId;
          if (messageId) {
            store.updateMessage(contextKey, messageId, {
              status: 'error',
              content: [
                {
                  type: 'error',
                  message: friendlyChatError({
                    code: data.code,
                    raw: data.message,
                    source: 'stream',
                    retryable: data.retryable,
                  }),
                  retryable: data.retryable,
                },
              ],
            });
          }
          store.setStreaming(contextKey, false);
          activeMessageIdRef.current = null;
          break;
        }
        case 'ping':
          break;
      }
    },
    [contextKey, startFlushTimer, stopFlushTimer, flushBuffer],
  );

  // The live ai-mcp-server can close the stream without `message_complete` /
  // `error` (e.g. its model call fails right after `message_start`). Finalize
  // any dangling streaming message so the UI doesn't strand a silent cursor:
  // no content → retryable error card; partial content → mark complete.
  const finalizeDanglingStream = useCallback(() => {
    const store = useAgentPanelStore.getState();
    const ctx = store.byContext[contextKey];
    const messageId = activeMessageIdRef.current ?? ctx?.streamingMessageId;
    if (!ctx?.isStreaming || !messageId) return;
    const msg = ctx.messages.find((m) => m.id === messageId);
    const hasContent =
      !!msg &&
      msg.content.some(
        (b) => (b.type === 'text' && b.text !== '') || (b.type === 'markdown' && b.markdown !== ''),
      );
    if (hasContent) {
      store.updateMessage(contextKey, messageId, { status: 'complete' });
    } else {
      store.updateMessage(contextKey, messageId, {
        status: 'error',
        content: [
          {
            type: 'error',
            message: 'The agent ended the stream without a response. Try again.',
            retryable: true,
          },
        ],
      });
    }
    store.setStreaming(contextKey, false);
    activeMessageIdRef.current = null;
  }, [contextKey]);

  const startStream = useCallback(
    (conversationId: string, content: string, extraBody?: Record<string, unknown>) => {
      abortRef.current?.abort();
      abortRef.current = createSSEStream({
        url: endpoints.chat.send(conversationId),
        baseUrl: mcpApiBaseUrl,
        body: { ...extraBody, content },
        onEvent: handleEvent,
        onError: (error) => {
          stopFlushTimer();
          const store = useAgentPanelStore.getState();
          const messageId =
            activeMessageIdRef.current ?? store.getOrInitContext(contextKey).streamingMessageId;
          if (messageId) {
            store.updateMessage(contextKey, messageId, {
              status: 'error',
              content: [
                {
                  type: 'error',
                  message: friendlyChatError({ raw: error, source: 'transport' }),
                  retryable: true,
                },
              ],
            });
          }
          store.setStreaming(contextKey, false);
          activeMessageIdRef.current = null;
        },
        onComplete: () => {
          stopFlushTimer(); // flushes buffered deltas first so finalize sees full content
          finalizeDanglingStream();
        },
      });
    },
    [contextKey, handleEvent, stopFlushTimer, finalizeDanglingStream],
  );

  const abortStream = useCallback(() => {
    abortRef.current?.abort();
    stopFlushTimer();
    const store = useAgentPanelStore.getState();
    const ctx = store.getOrInitContext(contextKey);
    if (ctx.streamingMessageId) {
      store.updateMessage(contextKey, ctx.streamingMessageId, { status: 'complete' });
    }
    store.setStreaming(contextKey, false);
    activeMessageIdRef.current = null;
  }, [contextKey, stopFlushTimer]);

  // Abort on unmount so a context unmount mid-stream doesn't leak the connection.
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      stopFlushTimer();
    };
  }, [stopFlushTimer]);

  return { startStream, abortStream };
}

/** Creates an empty content block of the given type for an opening SSE block event. */
function createEmptyBlock(blockType: ContentBlock['type']): ContentBlock | null {
  switch (blockType) {
    case 'text':
      return { type: 'text', text: '' };
    case 'markdown':
      return { type: 'markdown', markdown: '' };
    default:
      return null;
  }
}
